<template>
  <div class="timeslot-collection">

    <el-card v-if="timeslot_collection">

      <div slot="header" class="clearfix">
        <EditableText
          class="text-header"
          @update="onSetCollectionName($event)"
          :inline="true"
          :text="timeslot_collection.name"
          :id="timeslot_collection_id">
          <h2 :style="{ display: 'inline', color: timeslot_collection.color }" slot-scope="props">{{props.text}}</h2>
        </EditableText>

        <el-checkbox
          v-if="SHOW_DEBUG"
          style="float: right; margin: 0" size="small"
          v-model="include">include</el-checkbox>

        <el-button @click="$parent.deselectTab" size="small" type="info" class="close-button mr-2" plain>
          <i class="el-icon-close"></i>
        </el-button>

        <el-color-picker
          style="float: right; margin-right: 0.7em"
          size="small"
          :value="timeslot_collection.color"
          @update="onSetColor"
          @change="onSetColor"
          :predefine="predefineColors">
        </el-color-picker>
      </div>

      <!-- ----------------------------------------------------------------- -->

      <el-checkbox size="small" v-model="show_active_range">Show validity scope</el-checkbox>
      <el-checkbox size="small" v-model="show_inactive_items">Show inactive items</el-checkbox>

      <CollapseGroup title="Weekly regular timeslots" v-if="!isBlockerTSC">

        <el-row
         v-for="(pref, idx) in weekday_preferences_sorted"
         :key="'wkr-'+idx"
         class="weekday-pref"
         v-if="show_inactive_items || !pruned_weekday_preferences.includes( pref.id )">
          <el-col :span="2">

            <a href="#" @click.prevent="onRemoveRow(pref.id)">
              <font-awesome-icon icon="trash" />
            </a>
            #{{pref.num}}
            <template v-if="SHOW_DEBUG">
              <a class="up-down" href="#" @click.prevent="onSortUpDown('down', pref.id)" v-if="idx !== weekday_preferences_sorted.length - 1">&#9661;</a>
              <a class="up-down" href="#" @click.prevent="onSortUpDown('up', pref.id)" v-if="idx !== 0">&#9651;</a>
            </template>
            <span class="list-debug-slug" v-show="SHOW_DEBUG">{{pref.id.substr(0,7)}}</span>

          </el-col>
          <el-col :span="22" :style="{
            textDecoration:
              pruned_weekday_preferences.includes( pref.id ) ? 'line-through' : (
                partly_pruned_weekday_preferences.includes( pref.id ) ? 'line-through dotted var(--azure)' :
                  'none' )
              }">

            Plan every

            <div style="display: inline-block; line-height: 1em">
              <WeekdaySelector size="mini" :key="pref.hash" v-model="pref.selectedWeekdays" @save="onSave(pref.id, 'selectedWeekdays', $event)"></WeekdaySelector>
            </div>

            between

            <TimeSelector
              size="mini"
              v-model="pref.from"
              placeholder="Start time"
              :time-picker-options="getAllowedTimeRange('from', pref)"
              @save="onSave(pref.id, 'from', $event)"></TimeSelector>

            and

            <TimeSelector
              size="mini"
              v-model="pref.to"
              placeholder="End time"
              :time-picker-options="getAllowedTimeRange('to', pref)"
              @save="onSave(pref.id, 'to', $event)"></TimeSelector>.

            <div v-if="show_active_range" style="font-size: 8pt">
              This Rule is active after

              <DateSelector
                size="mini"
                v-model="pref.valid_after"
                placeholder="Pick a start day"
                :default-is-today="true"
                text-if-empty="always"
                :disable-before="current_date"
                @save="onSave(pref.id, 'valid_after', $event)"></DateSelector>

              and ends before

              <DateSelector
                size="mini"
                v-model="pref.valid_before"
                placeholder="Pick a end day"
                :default-is-today="true"
                :disable-before="pref.valid_after"
                @save="onSave(pref.id, 'valid_before', $event)"></DateSelector>.

            </div>

          </el-col>
        </el-row>

        <el-button size="mini" @click.prevent="onClickAddWeekdayPreference" >
          <font-awesome-icon icon="plus-circle" size="1x" />
          Add regular timeslot starting {{get_view_range_start.format('l')}}
        </el-button>
        <el-button size="mini" @click.prevent="onPrioritizeWeekdayPreferences">
          Set priorities
        </el-button>
        <!-- <el-button
          v-show="is_dirty"
          size="mini"
          type="success"
          @click="onSave('all')">Save</el-button> -->

      </CollapseGroup>

      <CollapseGroup :title="isBlockerTSC ? 'Blocked timeslots this week' : 'Exceptions this week'">
        <!-- TODO SORT BY DATE -->
        <el-row
          v-for="exception in exception_list_sorted"
          v-if="show_inactive_items || !pruned_exceptions.includes( exception.id )"
          :key="'exp-'+exception.id">
          <!-- {{exception.type}} -->

          <span :style="{
            textDecoration: pruned_exceptions.includes( exception.id ) ? 'line-through' : 'none',
            fontSize: '11pt'
            }">
            <template v-if="exception.type === 'DEL'">
              <font-awesome-icon icon="minus-square" />
              Del. timeslot on {{exception.date|shortDate}}
              <span v-if="SHOW_DEBUG" style="font-size: 8pt">(Rule: {{exception.ref}})</span>
              <a href="#" @click.prevent="onUndoException(exception.id)"><font-awesome-icon icon="trash" /></a>
            </template>

            <template v-if="exception.type === 'INS'">
              <font-awesome-icon icon="plus-square" />
              Ins. timeslot on {{exception.from|shortDate}}
              <strong>from</strong> {{exception.from|shortTime}}
              <strong>to</strong> {{exception.to|shortTime}}
              <a href="#" @click.prevent="onUndoException(exception.id)"><font-awesome-icon icon="trash" /></a>
            </template>

            <template v-if="exception.type === 'CHG'">
              <font-awesome-icon icon="pen-square" />
              <!-- {{exception.date|shortDate}} -->
              Changed timeslot on {{exception.new_from|shortDate}}
              <strong>from</strong> {{exception.new_from|shortTime}}
              <strong>to</strong> {{exception.new_to|shortTime}}
              <span v-if="SHOW_DEBUG" style="font-size: 8pt">(Rule: {{exception.ref}})</span>
              <a href="#" @click.prevent="onUndoException(exception.id)"><font-awesome-icon icon="trash" /></a>
            </template>
          </span>

        </el-row>
      </CollapseGroup>

      <CollapseGroup title="Assigned commitments" :initCollapse="false" v-if="!isBlockerTSC">
        <el-row v-for="commitment in related_commitments" :key="commitment.id">
          <el-col>
            <InlineCommitment :commitment="commitment" />
          </el-col>
        </el-row>
        <div v-if="related_commitments.length === 0" >
          <i class="el-icon-error" style="color: red"></i> No commitment assigned.
          <!-- TODO: button to add one -->
        </div>
      </CollapseGroup>

      <CollapseGroup title="Actionplanner settings" :initCollapse="true" v-if="!isBlockerTSC">
        <div style="margin-bottom: 0.75em">Select the type of effort you want us to prioritize:</div>
        <el-radio-group
          v-model="prioritizedEffortType"
          @change="onUpdatePrioritizedEffortType"
          size="mini">
          <el-radio label="COMMITMENT_TEMPLATE">Templates</el-radio><br>
          <el-radio label="COMMITMENT">Auto-schedule (Default)</el-radio><br>
          <el-radio label="SEQUENCE_TYPE1">Static Sequence</el-radio><br>
          <el-radio label="SEQUENCE_TYPE2">Dynamic Sequence</el-radio><br>
          <el-radio label="FIXED_TSC">Manual schedule</el-radio>
        </el-radio-group>
      </CollapseGroup>

      <hr>
      <el-button size="mini" type="danger" @click="onDelete">Delete Timeslot Collection</el-button>

      <pre v-if="SHOW_DEBUG">weekday_preferences_sorted: {{weekday_preferences_sorted}}</pre>

    </el-card>
    <!--
    <el-card v-else-if="timeslot_collection.deleted" class="content">
      Lane "<i>{{timeslot_collection.cachedName}}</i>" has been deleted
    </el-card>
    -->
    <div v-else class="content">
      <header>Loading...</header>
    </div>

    <!------------------------ tscPrioritiesModal -------------------------- -->
    <PrioritiesModal ref="tscPrioritiesModal" @save="onSavePriorities">
      <p>You can change the priority of weekly regular timeslots (weekday preferences) here.
        TODO: Explain how this information is used.</p>
    </PrioritiesModal>

  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { COLOR_LIST as predefineColors } from '@/misc/colors'
import WeekdaySelector from '@/components/Elements/WeekdaySelectorInline';
import TimeSelector from '@/components/Elements/TimeSelectorInline';
import DateSelector from '@/components/Elements/DateSelectorInline';
import EditableText from "@/components/Elements/EditableText";
import CollapseGroup from '@/components/Elements/CollapseGroup';
import InlineCommitment from '@/components/Inline/InlineCommitment';
import PrioritiesModal from '@/components/CalendarPage/Legacy/Elements/PrioritiesModal'
import moment from '@/moment-gp'
import clonedeep from 'lodash.clonedeep'
import util from '@/util'
import ConfirmDeletePromptMixin from "@/components/Mixins/Prompts/ConfirmDeletePrompt"

export default {
  mixins: [ ConfirmDeletePromptMixin ],
  props: {
    timeslot_collection: {
      type: Object,
      default: () => {return null}
    },
    timeslot_collection_id: {
      type: String,
      default: ""
    },
    isBlockerTSC: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    include: true, // TODO - raus?
    is_dirty: true, // TODO - raus?
    show_active_range: true,
    show_inactive_items: false,
    // weekday_preferences: null,
    predefineColors: predefineColors,
    prioritizedEffortType: null
  }),
  mounted() {
    if ( this.timeslot_collection.prioritizedEffortType ) {
      this.prioritizedEffortType = this.timeslot_collection.prioritizedEffortType;
    } else {
      this.prioritizedEffortType = 'COMMITMENT';
    }
    // this.onSave(true)
    // this.weekday_preferences =
    //   this.weekday_preferences_by_timeslot_collection_id_sorted( this.timeslot_collection_id );
  },
  computed: {
    ...mapGetters('timeslots', [
      'flattened_calendar_data_all',
    ]),
    ...mapGetters('calendar', [
      'get_view_range_start'
    ]),
    ...mapGetters('goal', [
      'effort_list_all',
      'effort_by_goal_id_and_effort_id'
    ]),

    timezone() {
      /* The big question here is, if timeslot collections should event support
         differnt timezones... probally not */
      // return this.user_get_timezone;
      return this.$models.userSettings.getters.user_get_timezone
    },

    current_date() {
      return moment.currentDate();
    },

    exception_list_sorted() {
      let list = this.$models.timeslotException
        .with({ timeslotCollectionId: this.timeslot_collection_id })
        .getList()
      return list ? list.itemsAsArray() : []
    },

    weekday_preferences_sorted() {
      let list =  this.$models.timeslotWeekdayPref
        .with({ timeslotCollectionId: this.timeslot_collection_id })
        .getList()
      return list ? list.itemsAsArray() : []
    },

    pruned_weekday_preferences() {
      let flattened_calendar_data = this.flattened_calendar_data_all[this.timeslot_collection_id];
      return flattened_calendar_data.pruned_weekday_preferences || [];
    },

    partly_pruned_weekday_preferences() {
      let flattened_calendar_data = this.flattened_calendar_data_all[this.timeslot_collection_id];
      return flattened_calendar_data.partly_pruned_weekday_preferences || [];
    },

    pruned_exceptions() {
      let flattened_calendar_data = this.flattened_calendar_data_all[this.timeslot_collection_id];
      return flattened_calendar_data.pruned_exceptions || [];
    },

    effort_list_all() {
      return this.$models.commitment.getters.commitment_list_all;
    },

    related_commitments() {
      return this.effort_list_all.filter(item => {
        return ( item.user.timeslotCollectionId === this.timeslot_collection_id );
      }).map(commitment => {
        return { ...commitment, $id: commitment.commitmentId }
      }).sort(util.sorter.by_user_prioidx);
    },
  },
  methods: {

    onClickAddWeekdayPreference() {
      console.log("onClickAddWeekdayPreference", this.get_view_range_start.format());
      this.$api.timeslot_collection_add_rule({
        timeslotCollectionId: this.timeslot_collection_id,
        rule: {
          sortidx: this.weekday_preferences_sorted.length + 1,
          selectedWeekdays: '--x--x-',
          from: '8:00',
          to: '10:00',
          valid_after: this.get_view_range_start.toISOString(), // TODO minus 1 day or TZ-Problem?
          valid_before: null,
        }
      })
    },

    onPrioritizeWeekdayPreferences() {
      let weekday_preferences_sortable =
        this.weekday_preferences_sorted.map((pref) => {
          return {
            id:    pref.id,
            name:  `#${pref.num}`,
            color: 'black'
          };
        });
      console.log("weekday_preferences_sortable", weekday_preferences_sortable);
      this.$refs.tscPrioritiesModal.open(weekday_preferences_sortable);
    },

    onSavePriorities( sorted_list ) {
      console.log("onSavePriorities", sorted_list);

      let sortidx = 1;
      let priorities = sorted_list.map(item => {
        return { id: item.id, sortidx: sortidx++ }
      });

      this.$api.timeslot_collection_set_rule_priorities({
        timeslotCollectionId: this.timeslot_collection_id,
        priorities
      }).then(() => {
        console.log("writing prio done");
      });
    },

    onSortUpDown(direction, ruleId) {
      // console.log("onSortUpDown", direction, index);
      // let new_index = index;
      // switch (direction) {
      //   case 'up':   new_index = index - 1; break;
      //   case 'down': new_index = index + 1; break;
      // }
      // this.weekday_preferences_sorted.move(index, new_index)

      // this.$api.timeslot_collection_update_rule({
      //   timeslotCollectionId: this.timeslot_collection_id,
      //   ruleId: ruleId,
      //   newRule: {
      //     sortidx: -1 // TODO
      //   }
      // })
      throw new Error('Not implemented');
    },

    onRemoveRow( ruleId ) {
      // console.log("onRemoveRow", index);
      // this.weekday_preferences_sorted.splice(index, 1);

      this.$api.timeslot_collection_rem_rule({
        timeslotCollectionId: this.timeslot_collection_id,
        ruleId: ruleId
      })
    },

    onUndoException( id ) {
      console.log("onUndoException", id);
      this.$api.timeslot_collection_rem_exception({
        timeslotCollectionId: this.timeslot_collection_id,
        exceptionId: id
      })
    },

    onSetCollectionName( newName ) {
      console.log("Updating collection title to ", this.timeslot_collection_id, newName);
      this.$api.timeslot_collection_set_name({
        timeslotCollectionId: this.timeslot_collection_id,
        name: newName
      });
    },

    onSetColor(color) {
      console.log("onSetColor", color)
      this.$api.timeslot_collection_set_color({
        timeslotCollectionId: this.timeslot_collection_id,
        color: color
      })
    },

    onDelete() {
      this.$confirm_delete_prompt('schedule' ).then(({ notify }) => {
        this.$emit('deleted', this.timeslot_collection_id);

        this.$api.timeslot_collection_delete({
          timeslotCollectionId: this.timeslot_collection_id,
          delete_mode: 'hard'
        }).then(notify)
      }).catch(e => e)
    },

    onUpdatePrioritizedEffortType(label) {
      console.log("onUpdatePrioritizedEffortType", label);

      this.$api.timeslot_collection_set_prioritized_effort_type({
        timeslotCollectionId: this.timeslot_collection_id,
        prioritizedEffortType: label
      })
    },

    onSave(prefId, field, event) {
      console.log("onSave", prefId, event);
      // TODO: Could be optimized - only save what has changed

      let updatedRule = this.weekday_preferences_sorted.find((item) => item.id === prefId);
      console.log('onSave updatedRule', updatedRule)
      updatedRule = clonedeep(updatedRule);

      if ( !updatedRule[field] ) {
        console.error("problem", event, updatedRule[field]);
      }

      if (field === "valid_after" || field === "valid_before") {
        //event = event.format("x") //format("YYYY-MM-DD")
      }
      console.log('onSave event', event)
      const VALID_FIELDS = ['from', 'to', 'selectedWeekdays', 'valid_after', 'valid_before'];

      if ( VALID_FIELDS.includes(field) ) {
        console.log('onSave saving')
        const updatedRuleValid = {};
        updatedRuleValid[field] = event;

        this.$api.timeslot_collection_update_rule({
          timeslotCollectionId: this.timeslot_collection_id,
          ruleId: prefId,
          newRule: updatedRuleValid
        }).then(() => {
          this.$store.commit("timeslots/INCREASE_UPDATE_COUNT")
        })
      }

      // const updatedRuleValid = {
      //   from:             updatedRule.from,
      //   to:               updatedRule.to,
      //   selectedWeekdays: updatedRule.selectedWeekdays,
      //   valid_after:      updatedRule.valid_after,
      //   valid_before:     updatedRule.valid_before
      // };

      // if ( !this.include ) {
      //   return {};
      // }

      // let weekday_preferences_sorted = clonedeep(this.weekday_preferences_sorted);
      // this.export_flat_weekday_list(weekday_preferences_sorted).then((flat_list) => {
      //   console.log("### FLAT LIST:", flat_list);
      // })
    },

    getAllowedTimeRange(type, pref) {
      // console.log("getAllowedTimeRange", type, pref);
      // TODO: Add 30 minutes
      if ( type === 'from' ) {
        return {
          start: '00:00',
          step:  '00:30',
          end:   pref.to,
        }
      }
      if ( type === 'to' ) {
        return {
          start: pref.from,
          step:  '00:30',
          end:   '24:00'
        }
      }
    }
  },
  components: {
    EditableText,
    WeekdaySelector,
    TimeSelector,
    DateSelector,
    CollapseGroup,
    PrioritiesModal,
    InlineCommitment
  }
}
</script>

<style>
.timeslot-collection .weekday-pref.el-row {
  margin-bottom: 0.5em;
}
.list-debug-slug {
  line-height: 1em;
  font-size: 8pt;
  color: grey;
  display: block;
}
a.up-down {
  color: black;
}
a.up-down:hover {
  text-decoration: none;
  color: var(--info);
}
</style>
